import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { TrendingUp, Search, Calendar, ArrowUp, Clock, Users, Download, FileSpreadsheet, FileText, Building2, FlaskConical, History, Plus, Trash2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeaveForecast() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [lastYear, setLastYear] = useState(null);
  const [showLastYear, setShowLastYear] = useState(false);
  const [loadingLastYear, setLoadingLastYear] = useState(false);
  const [whatIfLeaves, setWhatIfLeaves] = useState([]);
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [newLeave, setNewLeave] = useState({ start: '', end: '' });
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employees`, { headers });
        setEmployees(res.data.filter(e => e.employment_status === 'Active'));
      } catch {
        toast({ title: 'Error', description: 'Failed to load employees', variant: 'destructive' });
      } finally {
        setLoadingList(false);
      }
    };
    load();
  }, []);

  const loadForecast = useCallback(async (empId) => {
    setLoadingForecast(true);
    setSelectedEmp(empId);
    setLastYear(null);
    setShowLastYear(false);
    setWhatIfLeaves([]);
    try {
      const res = await axios.get(`${API}/api/leave-forecast/${empId}`, { headers });
      setForecast(res.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load forecast', variant: 'destructive' });
      setForecast(null);
    } finally {
      setLoadingForecast(false);
    }
  }, []);

  const toggleLastYear = async () => {
    if (showLastYear) { setShowLastYear(false); return; }
    if (lastYear) { setShowLastYear(true); return; }
    if (!selectedEmp) return;
    setLoadingLastYear(true);
    try {
      const res = await axios.get(`${API}/api/leave-forecast/comparison/${selectedEmp}`, { headers });
      setLastYear(res.data);
      setShowLastYear(true);
    } catch { /* silent */ }
    finally { setLoadingLastYear(false); }
  };

  // What-If calculations
  const whatIfByMonth = useMemo(() => {
    const byMonth = {};
    for (const lv of whatIfLeaves) {
      const start = new Date(lv.start + 'T00:00:00');
      const end = new Date(lv.end + 'T00:00:00');
      const d = new Date(start);
      while (d <= end) {
        // Count every calendar day, including weekends/dayoffs — when staff go
        // on vacation, dayoffs within the period are consumed as leave (matches
        // how leave balance & leave salary are calculated).
        const mo = d.getMonth() + 1;
        byMonth[mo] = (byMonth[mo] || 0) + 1;
        d.setDate(d.getDate() + 1);
      }
    }
    return byMonth;
  }, [whatIfLeaves]);

  const totalWhatIfDays = Object.values(whatIfByMonth).reduce((a, b) => a + b, 0);
  const hasWhatIf = whatIfLeaves.length > 0;

  // Adjusted forecast with what-if leaves
  const adjustedForecast = useMemo(() => {
    if (!forecast?.forecast) return [];
    let cumulativeDeduction = 0;
    return forecast.forecast.map(m => {
      cumulativeDeduction += (whatIfByMonth[m.month] || 0);
      return { ...m, balance_what_if: Math.round((m.balance_approved_only - cumulativeDeduction) * 10) / 10 };
    });
  }, [forecast, whatIfByMonth]);

  const addWhatIfLeave = () => {
    if (!newLeave.start || !newLeave.end || newLeave.start > newLeave.end) return;
    setWhatIfLeaves(prev => [...prev, { ...newLeave, id: Date.now() }]);
    setNewLeave({ start: '', end: '' });
  };

  const filtered = employees.filter(e =>
    !search || e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.employee_code?.toLowerCase().includes(search.toLowerCase()) ||
    e.department?.toLowerCase().includes(search.toLowerCase())
  );

  const hasPending = forecast && forecast.pending_leaves_total > 0;

  const chartData = useMemo(() => {
    return adjustedForecast.map(m => {
      const point = { name: m.month_name, 'Approved Only': m.balance_approved_only };
      if (hasPending) point['With Pending'] = m.balance_with_pending;
      if (hasWhatIf) point['What-If'] = m.balance_what_if;
      if (showLastYear && lastYear) {
        const lyMonth = lastYear.months.find(lm => lm.month === m.month);
        if (lyMonth) point[`${lastYear.year} Actual`] = lyMonth.balance;
      }
      return point;
    });
  }, [adjustedForecast, hasPending, hasWhatIf, showLastYear, lastYear]);

  // Get unique departments from active employees
  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))].sort();

  const handleDownload = async (type, format = 'excel') => {
    setDownloading(type);
    try {
      let url = '';
      let filename = '';
      if (type === 'individual') {
        if (!selectedEmp) {
          toast({ title: 'Select Employee', description: 'Please select an employee first', variant: 'destructive' });
          setDownloading(null);
          return;
        }
        url = `${API}/api/leave-forecast/download/individual/${selectedEmp}?format=${format}`;
        filename = `leave_forecast_${forecast?.employee_code || 'employee'}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (type.startsWith('dept_')) {
        const dept = type.replace('dept_', '');
        url = `${API}/api/leave-forecast/download/department/${encodeURIComponent(dept)}?format=${format}`;
        filename = `leave_forecast_${dept}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else {
        url = `${API}/api/leave-forecast/download/all?format=${format}`;
        filename = `leave_forecast_all.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      }
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast({ title: 'Downloaded', description: `Forecast exported as ${format.toUpperCase()}` });
    } catch {
      toast({ title: 'Error', description: 'Download failed', variant: 'destructive' });
    } finally {
      setDownloading(null);
      setShowDownloadMenu(false);
    }
  };

  if (loadingList) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-leave-forecast">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-indigo-600" />
            Leave Balance Forecast
          </h1>
          <p className="text-sm text-gray-500 mt-1">Project month-by-month leave balances for any employee</p>
        </div>
        <div className="relative">
          <Button onClick={() => setShowDownloadMenu(!showDownloadMenu)} variant="outline" className="gap-2" data-testid="download-menu-btn">
            <Download className="h-4 w-4" /> Download
          </Button>
          {showDownloadMenu && (
            <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-xl shadow-xl border z-50 p-2" data-testid="download-dropdown">
              {/* Individual */}
              <div className="px-3 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Individual Employee</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 text-xs gap-1.5" onClick={() => handleDownload('individual', 'excel')} disabled={!selectedEmp || !!downloading} data-testid="download-individual-excel">
                    <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" /> {downloading === 'individual' ? '...' : 'Excel'}
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-xs gap-1.5" onClick={() => handleDownload('individual', 'pdf')} disabled={!selectedEmp || !!downloading} data-testid="download-individual-pdf">
                    <FileText className="h-3.5 w-3.5 text-red-500" /> {downloading === 'individual' ? '...' : 'PDF'}
                  </Button>
                </div>
                {!selectedEmp && <p className="text-[10px] text-amber-500 mt-1">Select an employee first</p>}
              </div>

              <hr className="my-1" />

              {/* Department */}
              <div className="px-3 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Department-wise</p>
                <div className="max-h-36 overflow-y-auto space-y-1">
                  {departments.map(dept => (
                    <div key={dept} className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-700 flex-1 truncate">{dept}</span>
                      <button onClick={() => handleDownload(`dept_${dept}`, 'excel')} disabled={!!downloading} className="p-1 rounded hover:bg-emerald-50" data-testid={`download-dept-${dept}-excel`}>
                        <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
                      </button>
                      <button onClick={() => handleDownload(`dept_${dept}`, 'pdf')} disabled={!!downloading} className="p-1 rounded hover:bg-red-50" data-testid={`download-dept-${dept}-pdf`}>
                        <FileText className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="my-1" />

              {/* All */}
              <div className="px-3 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">All Employees</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 text-xs gap-1.5" onClick={() => handleDownload('all', 'excel')} disabled={!!downloading} data-testid="download-all-excel">
                    <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" /> {downloading === 'all' ? 'Generating...' : 'Excel'}
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-xs gap-1.5" onClick={() => handleDownload('all', 'pdf')} disabled={!!downloading} data-testid="download-all-pdf">
                    <FileText className="h-3.5 w-3.5 text-red-500" /> {downloading === 'all' ? 'Generating...' : 'PDF'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employee selector */}
        <div className="lg:col-span-1 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, code or department..."
              className="pl-9"
              data-testid="employee-search"
            />
          </div>
          <Card className="border-0 shadow-sm max-h-[600px] overflow-y-auto" data-testid="employee-list">
            <CardContent className="p-0 divide-y">
              {filtered.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No employees found</div>
              ) : (
                filtered.map(emp => (
                  <button
                    key={emp.id}
                    onClick={() => loadForecast(emp.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition hover:bg-gray-50 ${
                      selectedEmp === emp.id ? 'bg-indigo-50 border-l-3 border-indigo-500' : ''
                    }`}
                    data-testid={`emp-btn-${emp.employee_code}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {emp.photo_url ? (
                        <img src={`${API}${emp.photo_url}`} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold text-indigo-500">{emp.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                      <p className="text-[10px] text-gray-400">{emp.employee_code} &bull; {emp.department}</p>
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Forecast display */}
        <div className="lg:col-span-2 space-y-4">
          {!forecast && !loadingForecast && (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-20 text-center text-gray-400">
                <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                <p className="text-sm">Select an employee to view their leave balance forecast</p>
              </CardContent>
            </Card>
          )}

          {loadingForecast && (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-20 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
              </CardContent>
            </Card>
          )}

          {forecast && !loadingForecast && (
            <>
              {/* Employee info + stats */}
              <div className="flex items-center gap-3 px-1">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{forecast.employee_name}</h2>
                  <p className="text-xs text-gray-500">{forecast.employee_code} &bull; {forecast.department}</p>
                </div>
                <Badge variant="outline" className="ml-auto text-xs">{forecast.year}</Badge>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <Card className="border-0 shadow-sm" data-testid="stat-current">
                  <CardContent className="p-3 text-center">
                    <Calendar className="h-5 w-5 text-sky-500 mx-auto mb-1" />
                    <p className={`text-xl font-bold ${forecast.current_balance < 0 ? 'text-red-600' : 'text-sky-700'}`}>{forecast.current_balance}</p>
                    <p className="text-[9px] text-gray-400">Current Balance</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm" data-testid="stat-accrual">
                  <CardContent className="p-3 text-center">
                    <ArrowUp className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-emerald-700">+{forecast.monthly_accrual}</p>
                    <p className="text-[9px] text-gray-400">Monthly Accrual</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm" data-testid="stat-pending">
                  <CardContent className="p-3 text-center">
                    <Clock className="h-5 w-5 text-amber-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-amber-700">{forecast.pending_leaves_total}</p>
                    <p className="text-[9px] text-gray-400">Pending Days</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm" data-testid="stat-entitlement">
                  <CardContent className="p-3 text-center">
                    <Users className="h-5 w-5 text-indigo-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-indigo-700">{forecast.annual_entitlement}</p>
                    <p className="text-[9px] text-gray-400">Annual Entitlement</p>
                  </CardContent>
                </Card>
              </div>

              {/* What-If + Last Year Buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={showWhatIf ? "default" : "outline"}
                  className={`text-xs gap-1.5 ${showWhatIf ? 'bg-violet-600 hover:bg-violet-700' : ''}`}
                  onClick={() => setShowWhatIf(!showWhatIf)}
                  data-testid="admin-whatif-toggle"
                >
                  <FlaskConical className="h-3.5 w-3.5" /> What-If Planner
                </Button>
                <Button
                  size="sm"
                  variant={showLastYear ? "default" : "outline"}
                  className={`text-xs gap-1.5 ${showLastYear ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
                  onClick={toggleLastYear}
                  disabled={loadingLastYear}
                  data-testid="admin-lastyear-toggle"
                >
                  <History className="h-3.5 w-3.5" /> {loadingLastYear ? 'Loading...' : `${forecast.year - 1} Compare`}
                </Button>
              </div>

              {/* What-If Panel */}
              {showWhatIf && (
                <Card className="border-0 shadow-sm border-l-4 border-l-violet-500" data-testid="admin-whatif-section">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="h-4 w-4 text-violet-600" />
                      <h3 className="text-xs font-bold text-violet-700 uppercase tracking-wider">What-If Planner</h3>
                      {hasWhatIf && (
                        <Badge className="ml-auto text-[10px] bg-violet-100 text-violet-700 border-violet-200">-{totalWhatIfDays} days</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">Add hypothetical leave dates to simulate balance impact</p>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="text-[10px] text-gray-500 font-medium">From</label>
                        <Input type="date" value={newLeave.start} onChange={e => setNewLeave({ ...newLeave, start: e.target.value })} className="h-8 text-xs" data-testid="admin-whatif-start" />
                      </div>
                      <div className="flex-1">
                        <label className="text-[10px] text-gray-500 font-medium">To</label>
                        <Input type="date" value={newLeave.end} onChange={e => setNewLeave({ ...newLeave, end: e.target.value })} className="h-8 text-xs" data-testid="admin-whatif-end" />
                      </div>
                      <Button size="sm" className="h-8 px-3 bg-violet-600 hover:bg-violet-700" onClick={addWhatIfLeave} disabled={!newLeave.start || !newLeave.end || newLeave.start > newLeave.end} data-testid="admin-whatif-add">
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {whatIfLeaves.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {whatIfLeaves.map(lv => {
                          const s = new Date(lv.start + 'T00:00:00');
                          const e = new Date(lv.end + 'T00:00:00');
                          let days = 0;
                          const d = new Date(s);
                          while (d <= e) { days++; d.setDate(d.getDate() + 1); }
                          return (
                            <div key={lv.id} className="flex items-center gap-1.5 bg-violet-50 rounded-lg px-2.5 py-1">
                              <span className="text-xs text-violet-700">
                                {s.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} — {e.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                              </span>
                              <Badge className="text-[9px] bg-violet-200 text-violet-800">{days}d</Badge>
                              <button onClick={() => setWhatIfLeaves(prev => prev.filter(l => l.id !== lv.id))} className="p-0.5 text-violet-400 hover:text-red-500">
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Chart */}
              <Card className="border-0 shadow-sm" data-testid="forecast-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-600">Projected Balance — {forecast.year}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradApprovedAdmin" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0284c7" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#0284c7" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradPendingAdmin" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradWhatIfAdmin" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradLastYearAdmin" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} domain={['auto', 'auto']} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px' }} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <ReferenceLine y={0} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="3 3" label={{ value: '0', position: 'left', fontSize: 10, fill: '#ef4444' }} />
                        {showLastYear && lastYear && (
                          <Area type="monotone" dataKey={`${lastYear.year} Actual`} stroke="#14b8a6" fill="url(#gradLastYearAdmin)" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2 }} />
                        )}
                        <Area type="monotone" dataKey="Approved Only" stroke="#0284c7" fill="url(#gradApprovedAdmin)" strokeWidth={2} dot={{ r: 3 }} />
                        {hasPending && (
                          <Area type="monotone" dataKey="With Pending" stroke="#f59e0b" fill="url(#gradPendingAdmin)" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                        )}
                        {hasWhatIf && (
                          <Area type="monotone" dataKey="What-If" stroke="#7c3aed" fill="url(#gradWhatIfAdmin)" strokeWidth={2} strokeDasharray="3 3" dot={{ r: 3, fill: '#7c3aed' }} />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Table */}
              <Card className="border-0 shadow-sm" data-testid="forecast-table">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-600">Monthly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-gray-500 text-xs">
                        <th className="text-left py-2 font-medium">Month</th>
                        <th className="text-right py-2 font-medium">Accrual</th>
                        <th className="text-right py-2 font-medium">Approved</th>
                        {hasPending && <th className="text-right py-2 font-medium">Pending</th>}
                        <th className="text-right py-2 font-semibold text-sky-600">Balance</th>
                        {hasPending && <th className="text-right py-2 font-semibold text-amber-600">If Approved</th>}
                        {hasWhatIf && <th className="text-right py-2 font-semibold text-violet-600">What-If</th>}
                        {showLastYear && lastYear && <th className="text-right py-2 font-semibold text-teal-600">{lastYear.year}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {adjustedForecast.map((m, i) => {
                        const lyMonth = showLastYear && lastYear ? lastYear.months.find(lm => lm.month === m.month) : null;
                        const isNegApproved = m.balance_approved_only < 0;
                        const isNegPending = m.balance_with_pending < 0;
                        const isNegWhatIf = m.balance_what_if < 0;
                        return (
                          <tr key={m.month} className={`border-b border-gray-50 ${m.is_past ? 'bg-gray-50/60' : ''}`} data-testid={`admin-forecast-row-${m.month_name}`}>
                            <td className="py-2.5 font-medium text-gray-800">
                              {m.month_name}
                              {m.is_past && <span className="text-[9px] text-gray-400 ml-1.5">past</span>}
                            </td>
                            <td className="text-right py-2.5 text-emerald-600">{m.accrual > 0 ? `+${m.accrual}` : '-'}</td>
                            <td className="text-right py-2.5 text-red-500">{m.approved_deduction > 0 ? `-${m.approved_deduction}` : '-'}</td>
                            {hasPending && <td className="text-right py-2.5 text-amber-500">{m.pending_deduction > 0 ? `-${m.pending_deduction}` : '-'}</td>}
                            <td className={`text-right py-2.5 font-semibold ${isNegApproved ? 'text-red-600' : 'text-sky-700'}`}>{m.balance_approved_only}</td>
                            {hasPending && <td className={`text-right py-2.5 font-semibold ${isNegPending ? 'text-red-600' : 'text-amber-700'}`}>{m.balance_with_pending}</td>}
                            {hasWhatIf && (
                              <td className={`text-right py-2.5 font-semibold ${isNegWhatIf ? 'text-red-600' : 'text-violet-700'}`}>
                                {m.balance_what_if}
                                {whatIfByMonth[m.month] > 0 && <span className="text-[9px] text-violet-400 ml-0.5">(-{whatIfByMonth[m.month]})</span>}
                              </td>
                            )}
                            {showLastYear && lastYear && <td className="text-right py-2.5 font-semibold text-teal-700">{lyMonth?.balance ?? '-'}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* End of Year Summary */}
              <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-sky-50" data-testid="eoy-card">
                <CardContent className="p-5">
                  <h3 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2">End of Year Projection</h3>
                  <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                      <p className={`text-3xl font-bold ${adjustedForecast[adjustedForecast.length - 1]?.balance_approved_only < 0 ? 'text-red-600' : 'text-sky-700'}`}>
                        {adjustedForecast[adjustedForecast.length - 1]?.balance_approved_only}
                        <span className={`text-sm font-normal ml-1 ${adjustedForecast[adjustedForecast.length - 1]?.balance_approved_only < 0 ? 'text-red-400' : 'text-sky-400'}`}>days</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">Approved leaves only</p>
                    </div>
                    {hasPending && (
                      <div className="text-center">
                        <p className={`text-2xl font-bold ${adjustedForecast[adjustedForecast.length - 1]?.balance_with_pending < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {adjustedForecast[adjustedForecast.length - 1]?.balance_with_pending}
                          <span className={`text-xs font-normal ml-1 ${adjustedForecast[adjustedForecast.length - 1]?.balance_with_pending < 0 ? 'text-red-400' : 'text-amber-400'}`}>days</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">If pending approved</p>
                      </div>
                    )}
                    {hasWhatIf && (
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${adjustedForecast[adjustedForecast.length - 1]?.balance_what_if < 0 ? 'text-red-600' : 'text-violet-600'}`}>
                          {adjustedForecast[adjustedForecast.length - 1]?.balance_what_if}
                          <span className={`text-xs font-normal ml-1 ${adjustedForecast[adjustedForecast.length - 1]?.balance_what_if < 0 ? 'text-red-400' : 'text-violet-400'}`}>days</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">What-If scenario</p>
                      </div>
                    )}
                  </div>
                  {/* Carry-forward + LOP note */}
                  <div className="mt-4 pt-3 border-t border-indigo-100 text-xs" data-testid="eoy-carryforward-note">
                    {forecast.is_overdrawn ? (
                      <p className="text-gray-600">
                        <span className="font-semibold text-red-600">-{forecast.overdraw_lop_days} days</span> transferred to LOP record
                        <span className="mx-1.5 text-gray-300">·</span>
                        <span className="font-semibold text-gray-800">0 days</span> carry forward to next year
                        {forecast.return_month_name && (
                          <span className="text-gray-400"> (balance shown as 0 from the month after return in {forecast.return_month_name})</span>
                        )}
                      </p>
                    ) : (
                      <p className="text-gray-600">
                        <span className="font-semibold text-gray-800">{forecast.carry_forward_next_year} days</span> will carry forward to {forecast.year + 1} (max 15)
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
